import { Component, inject, signal } from '@angular/core'
import { FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms'
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog'
import { MatButtonModule } from '@angular/material/button'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { LucideFileText, LucideCircleAlert, LucideCheck } from '@lucide/angular'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { HotToastService } from '@ngxpert/hot-toast'

import { AuthService } from '../../../auth/services/auth.service'
import { AcceptTermsRequest } from '../../interfaces/user.interface'

@Component({
  selector: 'app-terms-of-service-modal',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatCheckboxModule,
    LucideFileText,
    LucideCircleAlert,
    LucideCheck,
    MatProgressSpinnerModule,
  ],
  templateUrl: './terms-of-service-modal.component.html',
  styleUrl: './terms-of-service-modal.component.scss',
})
export class TermsOfServiceModalComponent {
  private formBuilder = inject(FormBuilder)
  private authService = inject(AuthService)
  private toast = inject(HotToastService)
  private dialogRef = inject(MatDialogRef<TermsOfServiceModalComponent>)

  isAccepting = signal(false)

  termsForm = this.formBuilder.group({
    acceptTerms: [false, [Validators.requiredTrue]],
  })

  constructor () {
    this.dialogRef.disableClose = true
  }

  async onAccept (): Promise<void> {
    if (this.termsForm.valid && !this.isAccepting()) {
      this.isAccepting.set(true)

      const request: AcceptTermsRequest = {
        accepted: true,
      }

      try {
        const user = await this.authService.acceptTerms(request)
        if (user) {
          this.toast.success('Terms of Service accepted successfully!')
          this.dialogRef.close(true)
        }
      } catch (error: any) {
        let errorMessage = 'Failed to accept terms. Please try again.'
        if (error.error?.message) {
          errorMessage = error.error.message
        }

        this.toast.error(errorMessage)
      } finally {
        this.isAccepting.set(false)
      }
    } else {
      this.termsForm.markAllAsTouched()
    }
  }

  get isAcceptButtonDisabled(): boolean {
    return this.isAccepting() || this.termsForm.invalid;
  }

  get acceptTermsControl() {
    return this.termsForm.get('acceptTerms');
  }
}
